import { useCallback, useEffect, useState } from "react";
import { api, payload } from "./lib/api";

interface Status {
  configured: boolean;
  connected: boolean;
  canManage: boolean;
  propertyId: string | null;
  updatedAt: string | null;
}
interface Metrics {
  propertyId: string;
  range: { startDate: string; endDate: string };
  totals: { clicks: number; impressions: number; ctr: number; position: number };
  chart: { date: string; clicks: number; impressions: number }[];
  queries: { query: string; clicks: number; impressions: number; ctr: number; position: number }[];
}

const searchConsoleUrl = "https://search.google.com/search-console";
const businessProfileUrl = "https://business.google.com/locations";
const googleCloudCredentialsUrl = "https://console.cloud.google.com/apis/credentials";

export default function GoogleSearchConsolePanel({
  siteId,
  domain,
  admin,
}: {
  siteId: string;
  domain: string | null;
  admin: boolean;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const callbackUrl = `${window.location.origin}/api/integrations/google/callback`;

  const loadMetrics = useCallback(async () => {
    setError("");
    try {
      const result = await api<Metrics>(
        `/api/sites/${siteId}/integrations/google/metrics`,
      );
      setMetrics(result);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Impossibile leggere i dati Google",
      );
    }
  }, [siteId]);

  useEffect(() => {
    api<Status>(`/api/sites/${siteId}/integrations/google`)
      .then((next) => {
        setStatus(next);
        if (next.connected) void loadMetrics();
      })
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "Impossibile leggere Search Console",
        ),
      );
  }, [loadMetrics, siteId]);

  async function connect() {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ authorizationUrl: string }>(
        `/api/sites/${siteId}/integrations/google`,
        payload("POST"),
      );
      window.location.assign(result.authorizationUrl);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Collegamento non riuscito",
      );
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Scollegare Google Search Console da questo sito?"))
      return;
    setBusy(true);
    setError("");
    try {
      await api(
        `/api/sites/${siteId}/integrations/google`,
        payload("DELETE"),
      );
      setStatus((old) =>
        old ? { ...old, connected: false, propertyId: null } : old,
      );
      setMetrics(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Disconnessione non riuscita",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!domain && !admin) return null;
  if (!status && !error)
    return (
      <section className="gsc-panel gsc-loading">
        Preparazione dei servizi Google…
      </section>
    );
  if (error && !status)
    return (
      <section className="gsc-panel">
        <div className="notice error">{error}</div>
      </section>
    );

  if (!status?.configured) {
    if (!admin)
      return (
        <section className="google-setup-shell">
          <div className="google-setup-intro">
            <p className="eyebrow indigo">Google</p>
            <h3>Prepara la presenza del sito</h3>
            <p>
              Accedi ai servizi ufficiali con l’account proprietario
              dell’attività. Da qui puoi verificare il sito e gestire la sua
              presenza su Google e Maps.
            </p>
          </div>
          <GoogleActions domain={domain} />
        </section>
      );
    return (
      <section className="google-setup-shell admin-setup">
        <div className="google-setup-intro">
          <p className="eyebrow indigo">Configurazione Universe</p>
          <h3>Attiva OAuth Google una sola volta</h3>
          <p>
            Il flusso cliente è pronto. Mancano le credenziali OAuth della
            piattaforma e la chiave di cifratura nel Worker.
          </p>
          <a
            className="secondary google-external"
            href={googleCloudCredentialsUrl}
            target="_blank"
            rel="noreferrer"
          >
            Apri Google Cloud Console ↗
          </a>
        </div>
        <ol className="google-admin-steps">
          <li>Abilita la Search Console API nel progetto Google Cloud.</li>
          <li>Crea un client OAuth di tipo applicazione web.</li>
          <li>
            Registra come redirect autorizzato: <code>{callbackUrl}</code>
          </li>
          <li>Salva Client ID, Client Secret e chiave di cifratura nel Worker.</li>
        </ol>
      </section>
    );
  }

  if (!status.connected)
    return (
      <section className="google-setup-shell">
        <div className="google-setup-intro">
          <p className="eyebrow indigo">Google Search Console</p>
          <h3>Collega i dati del sito</h3>
          <p>
            Universe richiede soltanto l’accesso in lettura. Il cliente mantiene
            proprietà e controllo del proprio account Google.
          </p>
          {error && <div className="notice error">{error}</div>}
          {status.canManage && (
            <button className="primary" disabled={busy} onClick={connect}>
              {busy ? "Apertura Google…" : "Continua con Google"}
            </button>
          )}
        </div>
        <GoogleActions domain={domain} compact />
      </section>
    );

  return (
    <>
      <section className="gsc-panel gsc-connected">
        <div className="gsc-heading">
          <div>
            <p className="eyebrow indigo">Google Search Console</p>
            <h3>Prestazioni organiche</h3>
            <p>
              {metrics
                ? `${metrics.range.startDate} - ${metrics.range.endDate}`
                : "Collegamento attivo. In attesa dei dati della proprietà."}
            </p>
          </div>
          {status.canManage && (
            <button className="secondary" disabled={busy} onClick={disconnect}>
              Scollega
            </button>
          )}
        </div>
        {error && (
          <div className="google-property-help">
            <div className="notice error">{error}</div>
            <a href={searchConsoleUrl} target="_blank" rel="noreferrer">
              Verifica la proprietà su Google ↗
            </a>
            <button className="secondary" onClick={() => void loadMetrics()}>
              Riprova
            </button>
          </div>
        )}
      </section>
      <GoogleActions domain={domain} compact />
    </>
  );
}

function GoogleActions({
  domain,
  compact = false,
}: {
  domain: string | null;
  compact?: boolean;
}) {
  return (
    <div className={`google-actions ${compact ? "compact" : ""}`}>
      <article>
        <span className="google-product-mark search-mark">G</span>
        <div>
          <small>SEO e GEO</small>
          <strong>Google Search Console</strong>
          <p>
            {domain
              ? `Aggiungi e verifica ${domain} con l’account proprietario.`
              : "Aggiungi e verifica il dominio pubblico del sito."}
          </p>
        </div>
        <a href={searchConsoleUrl} target="_blank" rel="noreferrer">Apri ↗</a>
      </article>
      <article>
        <span className="google-product-mark business-mark">G</span>
        <div>
          <small>Presenza locale</small>
          <strong>Profilo dell’attività</strong>
          <p>Gestisci informazioni, posizione, foto e visibilità su Google e Maps.</p>
        </div>
        <a href={businessProfileUrl} target="_blank" rel="noreferrer">Gestisci ↗</a>
      </article>
    </div>
  );
}
